import {
  FilterQuery,
  ProjectFields,
  SortOption
} from '@yes.technology/react-toolkit'
import { useEffect, useMemo, useRef, useState } from 'react'

import { isEmpty } from 'lodash'
import { useObjectclass } from 'objectclass/shared'
import { useSearchParams } from 'react-router-dom'
import useNavigateMergingState from 'shared/hooks/useNavigateMergingState/useNavigateMergingState'
import { updateDisplayText } from 'shared/utils'
import {
  ConcatFields,
  extractFieldsFromConcatFields
} from 'shared/utils/object/concatenateFields'
import useStyleMaps from 'site/Renderer/hooks/useStyleMap'
import useMergedFilterQuery from 'site/shared/hooks/useMergedFilterQuery'
import { useSiteState } from 'siteState/shared'
import useBoundFilterQueryElementUuid from 'siteState/shared/hooks/useBoundFilterQueryElementUuid'
import { useSitemodel } from 'sitemodel/shared/hooks'
import { useFetchYesObjects } from 'yesObject/shared'
import { FetchYesObjectsResponse } from 'yesObject/shared/api/fetchYesObjects'
import useLocationState from '../FilterFields/hooks/useLocationState'
import DataTable from './DataTable'
import DataTableAreaLine from './DataTableAreaLine'
import { DataTablesProps } from './DataTables.types'
import useChangedFieldValues from './hooks/useChangedFieldValues'
import useInitialSortOptions from './hooks/useInitialSortOptions'
import useTableLayouts, { TableLayout } from './hooks/useTableLayouts'
import useTableMode from './hooks/useTableMode'
import { updateSelectedObjectInUrl as updateSelectedObjectInUrlUtil } from './utils/updateSelectedObjectInUrl'

export function setProjectFields(
  tableLayout: TableLayout,
  concatFields?: ConcatFields
): ProjectFields {
  let fields: ProjectFields = tableLayout.columnsLayouts.reduce(
    (acc: ProjectFields, column) => {
      acc[column.name] = 1
      return acc
    },
    {}
  )
  fields = tableLayout.extraColumns.reduce(
    (acc, column: string | { field_names: string[] }) => {
      if (typeof column === 'object' && 'field_names' in column) {
        column.field_names.forEach((fieldName: string) => {
          acc[fieldName] = 1
        })
        return acc
      }
      acc[column] = 1
      return acc
    },
    fields
  )

  const concatFieldsKeys = extractFieldsFromConcatFields(
    concatFields,
    Object.keys(fields)
  )

  if (concatFieldsKeys.length > 0) {
    concatFieldsKeys.forEach((field) => {
      fields[field] = 1
    })
  }

  return fields
}

const serializeProjectFields = (fields: ProjectFields) =>
  Object.keys(fields).sort().join('|')

const DataTables = ({ props }: DataTablesProps) => {
  const sitemodel = useSitemodel()

  const {
    elementUuid,
    uuid_objectclass: objectclassUuid,
    uuid_layout_classification: layoutClassificationUuid,
    objectclassQueryString = 'uuid_objectclass',
    informational = false,
    hide_main_collapsible_container: hideMainCollapsibleContainer = false,
    hide_row_action_buttons: hideRowActionButtons = false,
    hide_sort_buttons: hideSortButtons = false,
    merge_filter_query: manifestFilterQuery = {},
    nav_sticky: navSticky = false,
    allow_highlight_selected_row: allowHighlightSelectedRow = true,
    field_settings: fieldSettings,
    styleMap,
    is_show_title: isShowTitle,
    is_show_reports: isShowPDFReports,
    is_show_xls_report: isShowXLSReport,
    is_collapsible_card: isCollapsibleCard,
    objects = [],
    custom_action_buttons,
    navigationslider_background_color: navigationSliderBackgroundColor,
    navigationslider_color: navigationSliderColor,
    allow_collapsible_navigation_card_custom_colors:
      allowCollapsibleNavigationCardCustomColors,
    hide_collapsible_card_arrow: hideCollapsibleCardArrow = false,
    initial_pagination: initialPagination,
    map_column_to_action: mapColumnToAction = {},
    concat_fields: concatFields,
    isolate_filter_query: isolateFilterQuery = false,
    table_actions_visibility: tableActionsVisibility
  } = props

  const { objectclass, isLoading: isLoadingObjectClass } = useObjectclass({
    objectclassUuid
  })

  const { hasPersistentSelection } = useLocationState()

  const hasInitialObjects = objects?.length > 0

  const { fields = {} } = objectclass || {}

  const boundFilterQueryElementUuid = useBoundFilterQueryElementUuid(
    elementUuid,
    isolateFilterQuery
  )

  const [filterQuery, setFilterQuery] = useSiteState<FilterQuery>(
    'filter-query',
    boundFilterQueryElementUuid
  )

  const [refetchFilterQuery, setRefetchFilterQuery] = useSiteState(
    'refetch-filterquery',
    boundFilterQueryElementUuid
  )

  const [, setSharedProjectFields] = useSiteState<ProjectFields>(
    'project-fields',
    boundFilterQueryElementUuid
  )

  const styleProps = useStyleMaps(styleMap)

  const mergedFilterQuery = useMergedFilterQuery({
    filterQuery,
    manifestFilterQuery
  }) as FilterQuery

  const [memoizedFilterQuery, setMemoizedFilterQuery] =
    useState(mergedFilterQuery)

  const [_, setObjectclassUUID] = useSiteState<string>('objectclassuuid')
  const [shouldResetObjectArea, setShouldResetObjectArea] = useState(true)

  const [tableLayout] = useTableLayouts({ layoutClassificationUuid })

  const initialOrderBy = tableLayout.defaultOrderBy.map(
    ([key, value]) => ({ [key]: value }) as SortOption
  )

  const updatedFields = useMemo(
    () => updateDisplayText(fields, fieldSettings),
    [fieldSettings, fields]
  )

  const columns = useMemo(() => Object.keys(updatedFields), [updatedFields])

  const projectFields = useMemo(
    () => setProjectFields(tableLayout, concatFields),
    [tableLayout, concatFields]
  )

  const hasProjectFields = useMemo(
    () => Object.keys(projectFields).length > 0,
    [projectFields]
  )

  const projectFieldsSignatureRef = useRef<string>()

  useEffect(() => {
    if (!hasProjectFields) return

    const nextSignature = serializeProjectFields(projectFields)

    if (projectFieldsSignatureRef.current === nextSignature) return

    projectFieldsSignatureRef.current = nextSignature
    setSharedProjectFields(projectFields)
  }, [hasProjectFields, projectFields, setSharedProjectFields])

  const [lockObjectViewObject, setLockObjectViewObject] = useSiteState<boolean>(
    'lock-object-view-object'
  )

  useEffect(() => {
    setLockObjectViewObject(false)
  }, [memoizedFilterQuery, setLockObjectViewObject])

  const [searchParams] = useSearchParams()

  const navigateMergingState = useNavigateMergingState()

  const updateSelectedObjectInUrl = ({
    objects: responseObjects
  }: FetchYesObjectsResponse) => {
    const updatedSearchParams = updateSelectedObjectInUrlUtil({
      searchParams,
      responseObjects,
      hasPersistentSelection: hasPersistentSelection ?? false,
      lockObjectViewObject,
      hideRowActionButtons,
      shouldResetObjectArea,
      objectclassQueryString,
      objectclassUuid
    })

    const search = `?${updatedSearchParams.toString()}`

    const shouldFetchFromUuid = search.includes('object-uuid')

    setShouldResetObjectArea?.(false)

    navigateMergingState(
      {
        search,
        hash: ''
      },
      {
        state: {
          sortOptions,
          shouldFetchFromUuid: hasPersistentSelection && shouldFetchFromUuid
        },
        replace: true
      }
    )
  }

  const {
    yesObjects,
    isFetching: isLoadingObjects,
    pagination,
    totalObjects,
    setPage,
    sortOptions,
    setSortOptions,
    fetchYesObjects
  } = useFetchYesObjects({
    filterBody: {
      query: memoizedFilterQuery,
      objectclass: objectclassUuid,
      skip_metadata: true,
      ...(hasProjectFields ? { project_fields: projectFields } : {})
    },
    enabled: !isEmpty(memoizedFilterQuery),
    shouldMergeFilters: true,
    initialPagination,
    onFetch: updateSelectedObjectInUrl,
    staleTime: 0,
    gcTime: 0
  })

  useEffect(() => {
    if (pagination.offset === 0) {
      setMemoizedFilterQuery(mergedFilterQuery)
    }
  }, [mergedFilterQuery, pagination.offset])

  useEffect(() => {
    setPage(1)
  }, [setPage, mergedFilterQuery])

  const { mode, setMode } = useTableMode()
  const { changedFieldValues, setChangedFieldValues } = useChangedFieldValues()

  const sitemodelTitle = sitemodel?.title
  const objectclassName = objectclass?.des
  const title = sitemodelTitle || objectclassName || ''

  useInitialSortOptions(initialOrderBy, setSortOptions)

  useEffect(() => {
    setShouldResetObjectArea(true)
  }, [sortOptions, mergedFilterQuery, pagination])

  useEffect(() => {
    objectclassUuid && setObjectclassUUID(objectclassUuid)
  }, [objectclassUuid, setObjectclassUUID])

  useEffect(() => {
    if (!refetchFilterQuery) return
    fetchYesObjects()
    setRefetchFilterQuery(false)
  }, [fetchYesObjects, refetchFilterQuery, setRefetchFilterQuery])

  useEffect(() => {
    if (filterQuery || !mergedFilterQuery) return
    setFilterQuery(mergedFilterQuery)
  }, [filterQuery, mergedFilterQuery, setFilterQuery])

  const hasValidMergedFilterQuery =
    mergedFilterQuery && !isEmpty(mergedFilterQuery)

  const preventRendering = !hasInitialObjects && !hasValidMergedFilterQuery

  if (preventRendering) {
    return (
      <DataTableAreaLine
        hierarchyLevel='level_2'
        title={title}
        titleOnly
        disabled
        hideMainCollapsibleContainer
        style={styleProps}
        layoutClassificationUuid={layoutClassificationUuid}
      />
    )
  }

  const isLoading = isLoadingObjectClass || isLoadingObjects

  const finalObjects = hasValidMergedFilterQuery ? yesObjects : objects

  return (
    <>
      <DataTable
        navSticky={navSticky}
        idObjectClass={objectclassUuid}
        objects={finalObjects}
        refetchObjects={fetchYesObjects}
        onSort={setSortOptions}
        setCurrentPage={setPage}
        actionColumnsPosition='last'
        concatFields={props.concat_fields}
        customActionButtons={custom_action_buttons}
        displayLayoutSelection={false} // TODO: move to !informational or other behavior
        isShowPDFReports={isShowPDFReports && !isLoading}
        isShowXLSReport={isShowXLSReport && !isLoading}
        hideEditAction={tableActionsVisibility?.edit === false}
        hideInsertAction={tableActionsVisibility?.insert === false}
        hideSelectAndLinkAction
        {...{
          isLoading,
          informational,
          title,
          isShowTitle,
          isCollapsibleCard,
          objectclass,
          columns,
          fields: updatedFields,
          pagination,
          totalObjects,
          sortOptions,
          layoutClassificationUuid,
          mode,
          setMode,
          changedFieldValues,
          setChangedFieldValues,
          objectclassQueryString,
          hideMainCollapsibleContainer,
          hideRowActionButtons,
          hideSortButtons,
          allowHighlightSelectedRow,
          fieldSettings,
          navigationSliderBackgroundColor,
          navigationSliderColor,
          allowCollapsibleNavigationCardCustomColors,
          hideCollapsibleCardArrow,
          tableLayout,
          mapColumnToAction
        }}
        style={styleProps}
      />
    </>
  )
}

export default DataTables
